import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Card, CardBody, CardTitle, Row, Col } from 'reactstrap';
import { DRAWER_STYLES } from '../constants';

/**
 * MfaGuideDrawer component for displaying MFA guide content
 * @param {object} guide - Guide configuration object
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Close handler for the modal
 * @param {number} zIndex - Z-index for the modal
 */
const MfaGuideDrawer = ({ guide, isOpen, onClose, zIndex }) => {
  const { content } = guide;

  const getImageStyle = (style) => {
    switch (style) {
      case 'small':
        return DRAWER_STYLES.smallImage;
      case 'medium':
        return DRAWER_STYLES.mediumImage;
      case 'large':
      default:
        return DRAWER_STYLES.image;
    }
  };

  const renderImage = (image, index) => (
    <img 
      key={index}
      src={image.src} 
      alt={image.alt} 
      style={getImageStyle(image.style)} 
    />
  );

  const renderImages = (images) => {
    if (!images) return null;
    
    if (Array.isArray(images)) {
      return images.map((image, index) => renderImage(image, index));
    }
    
    return renderImage(images, 0);
  };

  const renderSteps = (steps) => {
    if (!steps) return null;
    
    return steps.map((step, index) => (
      <p key={index} style={{ marginBottom: 8 }}>
        {index + 1}. {step}
      </p>
    ));
  };

  const renderSections = (sections) => {
    if (!sections) return null;
    
    return sections.map((section, index) => (
      <div key={index}>
        <h6 style={{ fontWeight: 700, marginBottom: 4 }}>{section.title}</h6>
        <p style={{ marginBottom: 8 }}>{section.content}</p>
        {section.important && (
          <h6 style={{ fontWeight: 700, marginBottom: 4 }}>IMPORTANT:</h6>
        )}
        {section.important && (
          <p style={{ marginBottom: 8 }}>{section.important}</p>
        )}
        {section.image && renderImage(section.image)}
        {index < sections.length - 1 && <h6 style={{ fontWeight: 700, marginBottom: 4 }}>OR</h6>}
      </div>
    ));
  };

  const renderNote = (note) => {
    if (!note) return null;
    
    return (
      <>
        <h6 style={{ fontWeight: 700, marginTop: 16, marginBottom: 8 }}>{note.title}</h6>
        <p style={{ marginBottom: 8 }}>{note.content}</p>
        {note.image && renderImage(note.image)}
        {note.helpLink && (
          <p style={{ fontWeight: 700, marginTop: 16, cursor: 'pointer', color: 'blue' }}>
            {note.helpLink.text}<br />
            <a href={note.helpLink.url} style={{ color: '#1a0dab' }}>
              {note.helpLink.label}
            </a>
          </p>
        )}
      </>
    );
  };

  const renderImportant = (important) => {
    if (!important) return null;
    
    return (
      <>
        <h6 style={{ fontWeight: 700, marginTop: 16, marginBottom: 8 }}>{important.title}</h6>
        {Array.isArray(important.content) 
          ? important.content.map((item, index) => (
              <p key={index} style={{ marginBottom: 8 }}>{item}</p>
            ))
          : <p style={{ marginBottom: 8 }}>{important.content}</p>
        }
      </>
    );
  };

  const renderHelpLink = (helpLink) => {
    if (!helpLink) return null;
    
    return (
      <p style={{ fontWeight: 700, marginTop: 16, cursor: 'pointer', color: 'blue' }}>
        {helpLink.text}<br />
        <a href={helpLink.url} style={{ color: '#1a0dab' }}>
          {helpLink.label}
        </a>
      </p>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      className="modal-dialog-centered"
      style={{ zIndex }}
    >
      <ModalHeader toggle={onClose}>
        <h4 style={{ color: '#3b82f6' }}>{content.title}</h4>
      </ModalHeader>
      <ModalBody>
        <div style={{ gap: 0 }}>
          {content.description && (
            <p style={{ marginBottom: 8 }}>{content.description}</p>
          )}
          
          {content.steps && (
            <>
              <h6 style={{ color: '#3b82f6', fontWeight: 700, fontSize: 'lg', marginBottom: 8 }}>
                Here are the steps:
              </h6>
              {renderSteps(content.steps)}
            </>
          )}
          
          {content.sections && (
            <>
              <h6 style={{ color: '#3b82f6', fontWeight: 700, fontSize: 'lg', marginBottom: 8 }}>
                Here are the steps:
              </h6>
              {renderSections(content.sections)}
            </>
          )}
          
          {content.images && (
            <Row style={{ marginBottom: 8 }}>
              <Col>
                {renderImages(content.images)}
              </Col>
            </Row>
          )}
          
          {content.note && renderNote(content.note)}
          {content.helpLink && renderHelpLink(content.helpLink)}
          {content.important && renderImportant(content.important)}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button 
          color="dark" 
          onClick={() => {}} 
          style={DRAWER_STYLES.button}
        >
          Sign Up for Live Training
        </Button>
        <Button 
          color="primary" 
          onClick={onClose} 
          style={DRAWER_STYLES.closeButton}
        >
          CLOSE
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default MfaGuideDrawer; 